import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause } from "lucide-react";

const tracks = [
  { id: "aftermath", title: "Aftermath", bpm: "158", year: "2026", audio: "/audio/aftermath.mp3", img: "https://images.unsplash.com/photo-1573055592760-a1427683e07e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

const RowInner = ({ t, i, active, playing }) => (
  <>
    <div className="flex items-center gap-5 sm:gap-8 relative z-10">
      <span className={`font-mono text-xs ${active ? "text-ink/60" : "text-bone/40"}`}>
        {String(i + 1).padStart(2, "0")}
      </span>
      {t.audio && (
        <span
          className={`flex h-11 w-11 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
            active ? "border-ink bg-ink text-acid" : "border-bone/30 text-bone group-hover:border-acid"
          }`}
        >
          {playing ? <Pause className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" /> : <Play className="h-4 w-4 sm:h-5 sm:w-5 translate-x-[1px]" fill="currentColor" />}
        </span>
      )}
      <span
        className={`font-display font-black uppercase tracking-tighter text-3xl sm:text-5xl lg:text-6xl transition-colors duration-300 ${
          active ? "text-ink" : "text-bone"
        }`}
      >
        {t.title}
      </span>
    </div>
    <div className={`hidden sm:flex items-center gap-8 font-mono text-xs uppercase tracking-widest relative z-10 ${active ? "text-ink/70" : "text-bone/40"}`}>
      <span>{t.bpm} bpm</span>
      <span>{t.year}</span>
      <span className={`inline-block transition-transform duration-300 ${active ? "translate-x-1" : ""}`}>
        {t.audio ? (playing ? "playing" : "play") : "↗"}
      </span>
    </div>
    <AnimatePresence>
      {active && (
        <motion.img
          key="thumb"
          src={t.img}
          alt={t.title}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 0.25, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="pointer-events-none absolute right-24 top-0 h-full w-40 object-cover mix-blend-multiply"
        />
      )}
    </AnimatePresence>
  </>
);

const rowClass = (active) =>
  `group relative flex items-center justify-between border-t border-line px-5 sm:px-8 lg:px-12 py-7 sm:py-9 overflow-hidden transition-colors duration-300 text-left w-full ${
    active ? "bg-acid" : "bg-transparent"
  }`;

const Row = ({ t, i, hovered, setHovered, playingId, onToggle }) => {
  const playing = playingId === t.id;
  const active = hovered === t.id || playing;
  const motionProps = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] },
    onMouseEnter: () => setHovered(t.id),
    onMouseLeave: () => setHovered(null),
    className: rowClass(active),
  };

  if (t.audio) {
    return (
      <motion.button type="button" data-testid={`track-row-${t.id}`} onClick={() => onToggle(t)} {...motionProps}>
        <RowInner t={t} i={i} active={active} playing={playing} />
      </motion.button>
    );
  }
  return (
    <motion.a
      href="https://noisetrance.dance"
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`track-row-${t.id}`}
      {...motionProps}
    >
      <RowInner t={t} i={i} active={active} playing={false} />
    </motion.a>
  );
};

export const Sound = () => {
  const [hovered, setHovered] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!audioRef.current) audioRef.current = new Audio();
    const el = audioRef.current;
    const onEnded = () => setPlayingId(null);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("ended", onEnded);
      el.pause();
    };
  }, []);

  const onToggle = (t) => {
    const el = audioRef.current;
    if (playingId === t.id) {
      el.pause();
      setPlayingId(null);
      return;
    }
    if (el.src !== window.location.origin + t.audio) el.src = t.audio;
    el.play().then(() => setPlayingId(t.id)).catch(() => setPlayingId(null));
  };

  return (
    <section id="sound" className="relative bg-surface py-20 sm:py-28" data-testid="sound-section">
      <div className="px-5 sm:px-8 lg:px-12 mb-10 sm:mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-acid">/ Selected Works</div>
          <h2 className="font-display font-black uppercase text-bone text-4xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
            The sound
          </h2>
        </div>
        <p className="font-mono text-xs uppercase tracking-widest text-bone/40 max-w-xs">
          Latest transmission. Full catalogue lives on the label.
        </p>
      </div>
      <div className="border-b border-line">
        {tracks.map((t, i) => (
          <Row key={t.id} t={t} i={i} hovered={hovered} setHovered={setHovered} playingId={playingId} onToggle={onToggle} />
        ))}
      </div>
    </section>
  );
};

export default Sound;
